import { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  Backdrop,
  Button,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";

import StatisticCard from "./static-card";



import { useGetDashboardQuery } from "./dashboard.query";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

export const MyDashboard = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetDashboardQuery({
    year,
    activityDays: 15,
  });

  const years = useMemo(() => {
    return Array.from({ length: 6 }, (_, index) => currentYear - index);
  }, [currentYear]);

  const counts = data?.counts;

  const activityData = data?.mobile_activity ?? [];
  const reportsData = data?.reports_this_week ?? [];

  const activityRangeText =
    activityData.length > 0
      ? `${activityData[0]?.label} - ${activityData[activityData.length - 1]?.label}`
      : "No activity data";

  const stats = [

    {
      title: "Total Deputies",
      total: counts?.total_deputies ?? 0,

      redirectUrl: "/hr/deputy-user",
      percentage: 0,
    },
    {
      title: "Active Deputies",
      total: counts?.active_deputies ?? 0,

      redirectUrl: "/hr/deputy-user",
      percentage: 0,
    },
    {
      title: "Inactive Deputies",
      total: counts?.inactive_deputies ?? 0,

      redirectUrl: "/deputies/no-reports",
      percentage: 0,
    },
    {
      title: "Exempted From Reporting (No Reports)",
      total: counts?.exempted_deputies ?? 0,

      redirectUrl: "/hr/deputy-user",
      percentage: 0,
    },
    {
      title: "Total Public Users",
      total: counts?.total_public_users ?? 0,

      redirectUrl: "/hr/user",
      percentage: 0,
    },
    {
      title: "Total MTRCB Users",
      total: counts?.total_mtrcb_users ?? 0,

      redirectUrl: "/hr/mtrcb-users",
      percentage: 0,
    },

    {
      title: "Total Active Malls",
      total: counts?.total_malls ?? 0,

      redirectUrl: "/movie-house/malls",
      percentage: 0,
    },
    {
      title: "Total Active Cinemas",
      total: counts?.total_cinemas ?? 0,

      redirectUrl: "/movie-house/theaters",
      percentage: 0,
    },
  ];

  const hasError = Boolean(error);

  const errorMessage =
    error && typeof error === "object" && "error" in error
      ? String((error as { error?: string }).error)
      : "Failed to load dashboard data.";

  return (
    <Box>
      <Backdrop
        open={isLoading || isFetching}
        sx={{ color: "#fff", zIndex: 1300 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ pb: 5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 24, md: 28 },
                fontWeight: 800,
                color: "#0F172A",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Dashboard
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748B", mt: 0.25 }}>
              Overview for year {year}
            </Typography>
          </Box>

          <Select
            size="small"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{
              minWidth: 120,
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            {years.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {hasError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: "#FEF2F2",
              color: "#991B1B",
            }}
          >
            <Typography fontWeight={700}>
              Failed to load dashboard data.
            </Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatisticCard
                title={stat.title}
                total={stat.total}
                redirectUrl={stat.redirectUrl}
                percentage={stat.percentage}
              />
            </Grid>
          ))}

          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 750,
                      color: "#0F172A",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Pulse of the App
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#64748B", mt: 0.25 }}>
                    Daily active users — {activityRangeText}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: "#F3F4F6",
                    color: "#111827",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                      boxShadow: "none",
                    },
                  }}
                >
                  Last 15 Days
                </Button>
              </Box>

              <Box sx={{ width: "100%", height: 350 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={activityData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorActiveUsers"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#3B82F6"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#3B82F6"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#E5E7EB"
                    />

                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      dy={10}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      dx={-10}
                      allowDecimals={false}
                    />

                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                      }}
                    />

                    <Area
                      type="monotone"
                      dataKey="active_users"
                      name="Active Users"
                      stroke="#3B82F6"
                      fillOpacity={1}
                      fill="url(#colorActiveUsers)"
                      strokeWidth={2}
                      activeDot={{
                        r: 6,
                        fill: "#3B82F6",
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                height: "100%",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={3}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 750,
                      color: "#0F172A",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Reports Count
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#64748B", mt: 0.25 }}>
                    {data?.report_totals?.total ?? 0} total reports in {year}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    window.location.href = "/deputies/summary";
                  }}
                >
                  View Reports
                </Button>
              </Box>

              <Box sx={{ width: "100%", height: 350 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={reportsData}
                    margin={{ top: 20, right: 0, left: -20, bottom: 0 }}
                    barSize={12}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#E5E7EB"
                    />

                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      dy={10}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      allowDecimals={false}
                    />

                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                      }}
                    />

                    <Legend
                      iconType="circle"
                      wrapperStyle={{ paddingTop: 20 }}
                    />

                    <Bar
                      dataKey="Movie"
                      stackId="a"
                      fill="#0f03ff"
                    />

                    <Bar
                      dataKey="TV"
                      stackId="a"
                      fill="#00640a"
                    />

                    <Bar
                      dataKey="Others"
                      stackId="a"
                      fill="#ff0800"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};